import typeOf from "kind-of";
import { KeyValueItem, LabelKeyValueItem, LabelValueItem } from "@/model";


export const isEmpty = (value: any): boolean => {
    switch (typeOf(value)) {
        case "null":
        case "undefined":
            return true;
        case "boolean":
        case "number":
        case "function":
        case "date":
        case "regexp":
            return false;
        case "string":
        case "arguments":
            return value.length === 0;
        case "file":
        case "map":
        case "set":
            return value.size === 0;
        case "error":
            return value.message === "";
        case "array":
            if (value.length === 0) {
                return true;
            }
            return value.every(isEmpty);
        case "object":
            let objectValues = Object.values(value);
            if (objectValues.length === 0) {
                return true;
            }
            return objectValues.every(isEmpty);
        default:
            return true;
    }
};

const omit = (value: any) => {
    if (Array.isArray(value)) {
        value = value
            .map(omit)
            .filter(v => !isEmpty(v));
    }

    if (typeOf(value) === "object") {
        value = Object.fromEntries(
            Object.entries(value)
                .map(([k, v]) => ([k, omit(v)]))
                .filter(([_, v]) => v !== undefined)
        );
    }

    if (!isEmpty(value)) {
        return value;
    }
};

export const omitEmpty = (value: any) => {
    const ommitedValue = omit(value);
    if (ommitedValue === undefined) {
        return typeOf(value) === "object" ? {} : ommitedValue;
    }
    return ommitedValue;
};

export const dictToKeyValueArray = <T = any>(dict: Record<string, T>): KeyValueItem<T>[] => {
    return Object.entries(dict).map(([key, value]) => ({ key, value }));
};

export const keyValueArrayToDict = <T = any>(array: KeyValueItem<T>[]): Record<string, T> => {
    return Object.fromEntries(array.map(({ key, value }) => [key, value]));
};

export const getLabelKeyFromValue = <T>(items: LabelKeyValueItem<T>[], value: T) => {
    return items.find(item => item.value === value)?.labelKey ?? "";
};

export const getLabelFromValue = <T>(items: LabelValueItem<T>[], value: T) => {
    return items.find(item => item.value === value)?.label ?? "";
};
